import { useLazyQuery } from "@apollo/client/react"
import { Box } from "@mui/material"
import { useFormikContext } from "formik"
import { TriangleAlert } from "lucide-mui"
import React, { useEffect, useRef, useState, useCallback } from "react"

import { TextField } from "@/components/form"
import { GeocodeAddressDocument, type GeocodeAddressQuery, type GeocodeAddressQueryVariables } from "@/graphql/graphql"
import i18n from "@/i18n"
import { COUNTRY_COORDINATES, DEFAULT_COORDINATES } from "@/shared/country_coordinates"

import LocationMap from "./location_map"

type LocationProps = {
  apiKey?: string | null
  venueCountry?: string | null
}

const STYLES = {
  container: "bg-white rounded-lg p-6",
  header: "text-xl font-semibold mb-4 mt-0",
  twoColumnGrid: "grid grid-cols-2 gap-4",
} as const

const GEOCODE_CACHE_TTL_MS = 60_000

type GeocodeCacheEntry = {
  coords: { lat: number; lng: number } | null
  timestamp: number
}

const geocodeCache = new Map<string, GeocodeCacheEntry>()

const Location: React.FC<LocationProps> = ({ apiKey, venueCountry }) => {
  const { values, setFieldValue } = useFormikContext<{
    address: string
    city: string
    zipcode: string
    latitude: number | null
    longitude: number | null
  }>()

  const [geocodedCenter, setGeocodedCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [geocodeError, setGeocodeError] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const addressString = `${values.address} ${values.city} ${values.zipcode}`.trim()
  const prevAddressRef = useRef<string>(addressString)

  const [geocodeAddress] = useLazyQuery<GeocodeAddressQuery, GeocodeAddressQueryVariables>(GeocodeAddressDocument)

  useEffect(() => {
    if (prevAddressRef.current === addressString) return
    prevAddressRef.current = addressString

    if (!addressString) return

    void setFieldValue("latitude", null)
    void setFieldValue("longitude", null)
    setGeocodeError(false)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(async () => {
      const cached = geocodeCache.get(addressString)
      if (cached && Date.now() - cached.timestamp < GEOCODE_CACHE_TTL_MS) {
        if (cached.coords) {
          setGeocodedCenter(cached.coords)
          void setFieldValue("latitude", cached.coords.lat)
          void setFieldValue("longitude", cached.coords.lng)
          setGeocodeError(false)
        } else {
          setGeocodeError(true)
        }
        return
      }

      const result = await geocodeAddress({ variables: { address: addressString } })
      const coords = result.data?.geocodeAddress
      if (coords) {
        const entry = { lat: coords.latitude, lng: coords.longitude }
        geocodeCache.set(addressString, { coords: entry, timestamp: Date.now() })
        setGeocodedCenter(entry)
        void setFieldValue("latitude", coords.latitude)
        void setFieldValue("longitude", coords.longitude)
        setGeocodeError(false)
      } else {
        geocodeCache.set(addressString, { coords: null, timestamp: Date.now() })
        setGeocodeError(true)
      }
    }, 400)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [addressString, geocodeAddress, setFieldValue])

  const mapCenter = (() => {
    if (values.latitude != null && values.longitude != null) {
      return { lat: values.latitude, lng: values.longitude }
    }
    if (geocodedCenter) return geocodedCenter
    if (venueCountry) return COUNTRY_COORDINATES[venueCountry] ?? DEFAULT_COORDINATES
    return DEFAULT_COORDINATES
  })()

  const handleCoordinatesChange = useCallback(
    (lat: number, lng: number) => {
      void setFieldValue("latitude", lat)
      void setFieldValue("longitude", lng)
    },
    [setFieldValue],
  )

  return (
    <div className={STYLES.container} data-testid="settings-venue-location-container">
      <h2 className={STYLES.header}>{i18n.t("app.settings.venues.tabbio.location.header")}</h2>
      <div className="flex flex-col gap-4">
        {!!apiKey && (
          <Box mb={2}>
            <LocationMap center={mapCenter} onCoordinatesChange={handleCoordinatesChange} apiKey={apiKey} />
          </Box>
        )}
        {geocodeError ? (
          <div className="flex items-center gap-1 text-red-600 text-sm" data-testid="geocode-error">
            <TriangleAlert className="w-4 h-4 text-red-600" />
            <span>{i18n.t("app.settings.venues.tabbio.location.geocode_error")}</span>
          </div>
        ) : null}
        <TextField
          name="address"
          label={i18n.t("app.settings.venues.tabbio.location.address")}
          data-testid="settings-venue-location-input-address"
        />
        <div className={STYLES.twoColumnGrid}>
          <TextField
            name="city"
            label={i18n.t("app.settings.venues.tabbio.location.city")}
            data-testid="settings-venue-location-input-city"
          />
          <TextField
            name="zipcode"
            label={i18n.t("app.settings.venues.tabbio.location.zipcode")}
            data-testid="settings-venue-location-input-zipcode"
          />
        </div>
      </div>
    </div>
  )
}

export default Location
